import React from "react";
import { getRoleConstants } from "utils/game-information-constants";

// highlight color classnames for text.
export const HIGHLIGHTS = {
    positive: "text-green-700",
    negative: "text-red-700",
    neutral: "text-blue-700"
}


export const standardClassname = "space-y-2 text-sm";

export const keyWordClassname = "underline font-semibold";

export const GenericTooltips: { [key: string]: React.ReactNode } = {
  role: (
    <div className={standardClassname}>
      <p className=" font-semibold underline">Role</p>
      <p>
        A player's role determines their abilities, motivations, and monetary
        inscentives.
      </p>
      <p>
        There are 6 roles, divided across 2 estates. These are the public and
        private Hunters, public and private Reed Harvesters, public
        Stockbreeder, and the public Conservationist.
      </p>
      <p>
        Multiple players can occupy the same role. In which case,{" "}
        <span className={HIGHLIGHTS.neutral}>
          they will share the money and actions
        </span>
        , and must work together as a unit.
      </p>
    </div>
  ),
  cell: (
    <div className={standardClassname}>
      <p className=" font-semibold underline">Map Cells</p>
      <p>
        The map is made up of a number of cells (default is 100). Unless you
        play as the conservationist,{" "}
        <span className={HIGHLIGHTS.neutral}>
          only half will be visible to you
        </span>
        , which are the cells belonging to your estate.
      </p>
      <p>Cells are contained within a management unit.</p>
      <p>
        Cells can be acted upon using map actions, such as hunting, cutting,
        clearing, and grazing.
      </p>
      <p>
        Each cell is different, and has data associated with it. This is the
        biome, elevation, and water level. The water level will change each
        season depending on the water management regime chosen at Townhall.
      </p>
      <p>
        Cells may be inhabited by various species of birds based on biome and
        water level.
      </p>
    </div>
  ),
  "management unit": (
    <div className={standardClassname}>
      <p className=" font-semibold underline">Map Management Units</p>
      <p>
        The map is made up of a number of management units, which each contains
        at least two cells.
      </p>
      <p>
        These management units may be affected by certain actions, which affect
        every cell inside of them. These actions are embankment, disembankment,
        and the creation of a nature reservation.
      </p>
    </div>
  ),
  map: (
    <div className={standardClassname}>
      <p className=" font-semibold underline">The Map</p>
      <p>
        The map is the area that all of the players interact with. You can see
        it at the center of your screen. This shows the wetlands as it currently
        is at the present in-game year.
      </p>
      <p>
        The Map is split in half, with each half being dedicated to either the
        public or private eastate.{" "}
        <span className={HIGHLIGHTS.neutral}>
          You may only see the half of the map associated with your estate
        </span>{" "}
        unless you are playing as the conservationist.
      </p>
      <p>
        The map is made up of a number of Management Units, which themsevles are
        made up of cells.
      </p>
    </div>
  ),
  money: (
    <div className={standardClassname}>
      <p className=" font-semibold underline">Money</p>
      <p>
        All roles have money to use to perform certain actions, and to fulfill
        their upkeep.{" "}
        <span className={HIGHLIGHTS.neutral}>
          Money is shared by players in the same role
        </span>{" "}
        . It may also be transfered to someone else if you decide to do so. This
        is done on the bank tab.
      </p>
      <p>
        <span className={HIGHLIGHTS.positive}>
          Money transfers are very useful for brokering agreements with other
          players.
        </span>
      </p>
      <p>Only some roles count money into their end game scoring.</p>
    </div>
  ),
  townhall: (
    <div className={standardClassname}>
      <p className=" font-semibold underline">Townhall</p>
      <p>
        At the end of every year/round, all of the players go to the Townhall.
        At townhall, everyone votes on which Water Management Regime will govern
        the wetlands.
      </p>
      <p>Each player gets 1 vote, and it's winner-takes-all!</p>
      <p>
        After the voting is done, the year advances, and the players are brought
        back to the map screen, where they can view the results of the year.
      </p>
    </div>
  ),
  "water regime": (
    <div className={standardClassname}>
      <p className=" font-semibold underline">Water Management Regime</p>
      <p>
        A Water Management Regime is a plan for how much to flood the wetlands
        each season. It is selected at Townhall every year, or through
        embankment.
      </p>
      <p>
        Seasonal waters are essential to managing the wetlands. Dry summers can
        dry the wetlands up, but too much water will flood it.
      </p>
      <p>
        <span className={HIGHLIGHTS.neutral}>
          Most birds prefer moderate amounts of water.
        </span>
      </p>
      <p>
        <span className={HIGHLIGHTS.negative}>
          If the water level of a cell is above 20 cm during a season, reed
          harvesting and grazing becomes impossible for that season!!
        </span>{" "}
        Grazing happens every season, and reed harvesting happens in the winter.
      </p>
      <img src="/Images/WLregime.png"></img>
    </div>
  ),
  "estate": (
    <div className={standardClassname}>
      <p className=" font-semibold underline">The Estates</p>
      <p>
        There are two estates in the game, representing either public or private ownership of land.
      </p>
      <p>
        The public estate has 4 roles, so it tends to have higher levels of competition for control. The private estate on the otherhand has bonuses to hunting, and is much more profit based in general.
      </p>
      <p>
        Members of an estate can only see that estate's half of the map.
      </p>
    </div>
  ),
};
export const BirdTooltips: { [key: string]: React.ReactNode } = {
  DUCK: (
    <div>
      <p className=" font-semibold underline">Ducks</p>
      <p>Loves wet biomes, as long as the water isnt too deep.</p>
      <p><span className={HIGHLIGHTS.positive}>Loves</span><span className={HIGHLIGHTS.neutral}> open water, meadow, and meadow reed.</span> Can also be found in reed water.</p>
      <p>Ducks have a very durable population. <span className={HIGHLIGHTS.neutral}>They can be hunted without harming the long term duck population.</span></p>
      <p>They are very important for the hunter role, <span className={HIGHLIGHTS.negative}>who hunts them for money.</span></p>
    </div>
  ),
  BOOMING_BITTERN: (
    <div>
      <p className=" font-semibold underline">Booming Bittern</p>
      <p>Very <span className={HIGHLIGHTS.neutral}>ecologically important</span> and <span className={HIGHLIGHTS.negative}>at risk species.</span></p>
      <p>Roams around the map. Each icon represents a fractional bittern.</p>
      <p><span className={HIGHLIGHTS.positive}>Loves</span><span className={HIGHLIGHTS.neutral}> reedbeds, reed shrubs, and reed water.</span></p>
      <p>But in order for them to show up, there must be <span className={HIGHLIGHTS.neutral}>slight flooding</span>.</p>
      <p>As a conservationist, this is an important species to care for.</p>
    </div>
  ),
  PURPLE_HERON_COLONY: (
    <div>
      <p className=" font-semibold underline">Purple Heron Colony</p>
      <p>Very <span className={HIGHLIGHTS.neutral}>ecologically important</span> and <span className={HIGHLIGHTS.negative}>at risk species.</span></p>
      <p>Each icon represents part of the domain of one or more colonies of herons. The colony is spread out over the wetlands.</p>
      <p><span className={HIGHLIGHTS.positive}>Loves</span><span className={HIGHLIGHTS.neutral}> reedbeds, reed shrubs, and reed water.</span></p>
      <p>But in order for them to show up, there must be <span className={HIGHLIGHTS.neutral}>slight to moderate flooding</span>, with them prefering moderate.</p>
      <p>As a conservationist, this is an important species to care for.</p>
    </div>
  )
}
export const RoleToolTips: { [key: string]: React.ReactNode } = {
  "Reed Harvester": (
    <div className={standardClassname}>
      <p className=" font-semibold underline">
        Reed Harvester (Public/Private)
      </p>
      <p>
        Reed Harvesters make their money harvesting reeds from the wetlands.
        They are both in the public and private sector.
      </p>
      <p>
        This role has{" "}
        <span className={HIGHLIGHTS.negative}>a lot of passive expenses</span>,
        so they need to do a lot of harvesting in order to make a profit. But a
        well managed wetlands can be extremely profitable.
      </p>
      <p>
        They want to{" "}
        <span className={HIGHLIGHTS.positive}>maximize Reedbed land</span> in
        order to maximize their income.
      </p>
      <p>
        The private reed harvester tends to have an easier time, because they
        have a lot less competition over land.
      </p>
    </div>
  ),
  Stockbreeder: (
    <div className={standardClassname}>
      <p className=" font-semibold underline">Stockbreeder (Public)</p>
      <p>
        The stockbreeder breeds cattle for profit. In order to do this, they
        must graze the cattle on good land to minimize their overhead costs.
      </p>
      <p>
        They want to maximize grazable land such as meadows. This is essential
        to having a large herd.
      </p>
      <p>
        A well managed herd can be extremely profitable. The ability to buy and
        sell cows at any time allows for a degree of adaptability.
      </p>
    </div>
  ),
  "Conservationist": (
    <div className={standardClassname}>
      <p className=" font-semibold underline">Conservationist (Public)</p>
      <p>
        The Conservationist is primarily concerned with the ecological health of
        the wetlands. As such,{" "}
        <span className={HIGHLIGHTS.neutral}>
          They are the only roll that can see the map of both estates.
        </span>
        .
      </p>
      <p>
        They want to maximize bird populations and habitats{" "}
        <span className={HIGHLIGHTS.positive}>such as reedbeds</span>.
      </p>
      <p>
        The conservationist does not have a lot of income. As such, they must{" "}
        <span className={HIGHLIGHTS.neutral}>
          negotiate and deal with other players
        </span>{" "}
        in order to achieve their objectives.
      </p>
    </div>
  ),
  "Public Hunter": (
    <div className={standardClassname}>
      <p className=" font-semibold underline">Hunter (Public)</p>
      <p>
        The public hunter makes their money by hunting ducks. The more ducks
        they hunt, the more money they make with no limit.
      </p>
      <p>
        They want to maximize the duck population and their habitats in order to
        make as much money as possible.
      </p>
      <p>
        Hunting in the private estate is generally more successful due to the
        relative seclusion, and the private estate has much less competition
        over land. Because of this, public hunters can become very wealthy.
      </p>
    </div>
  ),
  "Private Hunter": (
    <div className={standardClassname}>
      <p className=" font-semibold underline">Hunter (Private)</p>
      <p>
        The public hunter makes their money by hunting ducks. This is to control the duck population, and their activities are subsidised by the government.
      </p>
      <p>Public hunters have a limit to how much they can make hunting, and their income comes in brackets. Despite this, they do not have any overhead costs, so they are able to focus more on achieving their objectives than trying to stay solvent.</p>
      <p>They want to maximize the duck population and habitat.</p>
    </div>
  ),
};

// hunting tooltip must be separate so private and public point to the same one.
export const ActionTooltips: { [key: string]: React.ReactNode } = {
  clearing: (
    <div className={standardClassname}>
      <p className=" font-semibold underline">Clearing tool</p>
      <p>
        Clear foilage to{" "}
        <span className={HIGHLIGHTS.positive}>transform the biome</span> of a
        cell.
      </p>
      <p>
        Can{" "}
        <span className={HIGHLIGHTS.positive}>improve the productivity</span> of
        overgrown biomes.
      </p>
      <p>
        Will also <span className={HIGHLIGHTS.negative}>destroy and flood</span>{" "}
        reedbeds.
      </p>
    </div>
  ),
  cut: (
    <div className={standardClassname}>
      <p className=" font-semibold underline">Cutting tool</p>
      <p>
        Cut reeds to <span className={HIGHLIGHTS.positive}>make money</span>.
      </p>
      <p>
        Cutting occurs in the winter. Water level must not be above 20 in the winter{" "}
        <span className={HIGHLIGHTS.negative}>or it will be impossible to harvest that cell this year!</span>
      </p>
      <p>
        Cutting reeds for 3 years straight can make them{" "}
        <span className={HIGHLIGHTS.negative}>decay</span> into{" "}
        <span className={HIGHLIGHTS.neutral}>Reed Water</span>.
      </p>
      <p>
        Cutting reed water will make it{" "}
        <span className={HIGHLIGHTS.negative}>decay</span> into{" "}
        <span className={HIGHLIGHTS.neutral}>Open Water</span>.
      </p>
    </div>
  ),
  grazing: (
    <div className={standardClassname}>
      <p className=" font-semibold underline">Grazing tool</p>
      <p>
        Graze your herd on a cell to{" "}
        <span className={HIGHLIGHTS.positive}>reduce cattle upkeep</span>.
      </p>
      <p>
        Biomes have different grazing values, with{" "}
        <span className={HIGHLIGHTS.neutral}>Meadow</span> being the best.
      </p>
      <p>
        Grazing Reedbeds will<span className={HIGHLIGHTS.neutral}>destroy</span>{" "}
        them and transform it into a meadow.
      </p>
      <p>
        Grazing is also useful to{" "}
        <span className={HIGHLIGHTS.positive}>maintain certain biomes</span>{" "}
        such as <span className={HIGHLIGHTS.neutral}>meadows and shrubs</span>
      </p>
    </div>
  ),
  "private-hunt": (
    <div className={standardClassname}>
      <p className=" font-semibold underline">Hunting tool</p>
      <p>
        Hunt ducks to <span className={HIGHLIGHTS.positive}>make money.</span>.
      </p>
      <p>
        You make{" "}
        <span className={HIGHLIGHTS.neutral}>
          ${getRoleConstants("Hunter", "private")?.valuePerDuck}
        </span>{" "}
        per duck hunted
      </p>
    </div>
  ),
  "public-hunt": (
    <div className={standardClassname}>
      <p className=" font-semibold underline">Hunting tool</p>
      <p>
        Hunt ducks to{" "}
        <span className={HIGHLIGHTS.positive}>increase your income</span>.
      </p>
      <p>
        The maximum income is reached at{" "}
        <span className={HIGHLIGHTS.neutral}>150 ducks.</span>
      </p>
    </div>
  ),
  embank: (
    <div className={standardClassname}>
      <p className=" font-semibold underline">Embanking tool</p>
      <p>
        Isolate a zone, forcing the use of a single exclusive{" "}
        <span className={HIGHLIGHTS.neutral}>water managment regime</span>.
      </p>
      <p>
        <span className={HIGHLIGHTS.negative}>Expensive</span>, so it might make
        sense to collaborate with other roles.
      </p>
      <p>
        Can cause upstream zones to become land locked,{" "}
        <span className={HIGHLIGHTS.negative}>
          preventing important seasonal flooding
        </span>
        .
      </p>
    </div>
  ),
  disembank: (
    <div className={standardClassname}>
      <p className=" font-semibold underline">Disembanking tool</p>
      <p>
        Undo an embankment. Costs exactly half as much as it did to embank it.
      </p>
    </div>
  ),
  "create-nature-reserve": (
    <div className={standardClassname}>
      <p className=" font-semibold underline">Nature Reserve tool</p>
      <p>
        Create a nature reserve. The rules and regulations of the reserve are to
        be{" "}
        <span className={HIGHLIGHTS.neutral}>agreed upon by the players</span>.{" "}
      </p>
      <p>
        Funding may have to be aquired from the government (the host) or from
        other players.
      </p>
      <p>
        This is useful espeically to{" "}
        <span className={HIGHLIGHTS.positive}>protect bird populations</span>.
      </p>
    </div>
  ),
};
export const LandToolTips: { [key: string]: React.ReactNode } = {
  "open water": (
    <div className={standardClassname}>
      <p className=" font-semibold">
        Open water: Large expanses of water with minimal vegetation.
      </p>
      <p>
        Has the <span className={HIGHLIGHTS.positive}>most ducks</span> of any
        biome, and{" "}
        <span className={HIGHLIGHTS.negative}>little else of value</span>.
      </p>
      <p>
        Will <span className={HIGHLIGHTS.positive}>grow reeds</span> if the
        water is drained in summer for 3 years.
      </p>
    </div>
  ),
  meadow: (
    <div className={standardClassname}>
      <p className=" font-semibold">
        Meadow: Open grassy areas with low vegetation.
      </p>
      <p>
        This is the{" "}
        <span className={HIGHLIGHTS.positive}>best grazing land</span>.
      </p>
      <p>
        There are <span className={HIGHLIGHTS.positive}>many ducks</span> in the
        meadows.
      </p>
      <p>
        Meadows will{" "}
        <span className={HIGHLIGHTS.neutral}> transition to reedbeds</span> if
        not grazed.
      </p>
      <p>
        <span className={HIGHLIGHTS.negative}>Shrubs will grow</span> if
        untended and dry for a year.
      </p>
    </div>
  ),
  "meadow shrubs": (
    <div className={standardClassname}>
      <p className=" font-semibold">
        Meadow shrubs: Meadows with scattered shrubbery.
      </p>
      <p>
        This is <span className={HIGHLIGHTS.positive}>decent graze land</span>.
      </p>
      <p>
        Can be <span className={HIGHLIGHTS.neutral}>cleared</span> to become the
        better grazing <span className={HIGHLIGHTS.positive}>meadow</span>.
      </p>
      <p>
        If kept dry and unused for 5 years, it will{" "}
        <span className={HIGHLIGHTS.negative}>decay</span> into a forest.
      </p>
    </div>
  ),
  "meadow reed": (
    <div className={standardClassname}>
      <p className=" font-semibold">
        Meadow reed: Meadows with reed plants, often near water.
      </p>
      <p>
        This is <span className={HIGHLIGHTS.positive}>good graze land</span>.
      </p>
      <p>
        There are <span className={HIGHLIGHTS.positive}>lots of ducks</span>{" "}
        here.
      </p>
      <p>
        It is not stable, and will either turn into a{" "}
        <span className={HIGHLIGHTS.neutral}>meadow</span> if grazed, or a{" "}
        <span className={HIGHLIGHTS.neutral}>reedbed</span> if not.
      </p>
    </div>
  ),
  reedbed: (
    <div className={standardClassname}>
      <p className=" font-semibold">
        Reedbed: Dense areas dominated by reeds, typically wet.
      </p>
      <p>
        The reeds are <span className={HIGHLIGHTS.positive}>harvestable</span>{" "}
        by Reed Harvesters for{" "}
        <span className={HIGHLIGHTS.positive}>large profits</span>.
      </p>
      <p>
        <span className={HIGHLIGHTS.positive}>Decent grazing lands</span> but
        grazing will <span className={HIGHLIGHTS.negative}>destroy it</span>,
        turning it into a meadow.
      </p>
      <p>
        <span className={HIGHLIGHTS.positive}>Important habitat</span> for{" "}
        <span className={HIGHLIGHTS.neutral}>bitterns and herons</span>, but
        herons are <span className={HIGHLIGHTS.negative}>scared off</span> by
        reed harvesting.
      </p>
      <p>
        Can <span className={HIGHLIGHTS.negative}>decay</span> into reed shrubs
        if untended and dry, into or reed water if{" "}
        <span className={HIGHLIGHTS.negative}>over harvested</span> for 3 years.
      </p>
    </div>
  ),
  "reed shrubs": (
    <div className={standardClassname}>
      <p className=" font-semibold">
        Reed shrubs: Mixture of reeds and shrubs in wet areas.
      </p>
      <p>
        <span className={HIGHLIGHTS.positive}>Okay Graze lands</span>. Can be
        cleared for better grazing, but is sustainable for birds when grazed as
        is.
      </p>
      <p>
        Makes a <span className={HIGHLIGHTS.positive}>good habitat</span> for{" "}
        <span className={HIGHLIGHTS.neutral}>bitterns and herons</span>.
      </p>
      <p>
        <span className={HIGHLIGHTS.negative}>Decays</span> into a forest if dry
        and unused for 5 years, or{" "}
        <span className={HIGHLIGHTS.negative}>erodes</span> into open water if
        flooded for 3 years.
      </p>
    </div>
  ),
  "reed water": (
    <div className={standardClassname}>
      <p className=" font-semibold">
        Reed water: Water areas with abundant reed growth. Ecologically
        delicate.
      </p>
      <p>
        <span className={HIGHLIGHTS.positive}>Okay Graze lands</span>.
      </p>
      <p>
        Reed water can be{" "}
        <span className={HIGHLIGHTS.positive}>cut for profit</span> by reed
        harvesters, but this will{" "}
        <span className={HIGHLIGHTS.negative}>destroy it</span>, making it{" "}
        <span className={HIGHLIGHTS.neutral}>open water</span>.
      </p>
      <p>
        This is an{" "}
        <span className={HIGHLIGHTS.positive}>excellent habitat</span> for{" "}
        <span className={HIGHLIGHTS.neutral}>ducks, bitterns, and herons</span>.
        Herons specifically <span className={HIGHLIGHTS.positive}>love</span>{" "}
        this biome.
      </p>
      <p>
        If left alone for 3 dry summers, it will{" "}
        <span className={HIGHLIGHTS.positive}>transform</span> into a reedbed. 3
        wet years will turn it into open water.
      </p>
    </div>
  ),
  forest: (
    <div className={standardClassname}>
      <p className=" font-semibold">
        Forest: Densely wooded areas with significant tree coverage.
      </p>
      <p>
        <span className={HIGHLIGHTS.negative}>Not especially useful</span> for
        anyone. A ripe candidate for development.
      </p>
      <p>
        Can be cleared to become a{" "}
        <span className={HIGHLIGHTS.positive}>meadow</span>.
      </p>
    </div>
  ),
};




export const ToolTypes: { [key: string]: { [key: string]: React.ReactNode } } = {
  action: ActionTooltips,
  land: LandToolTips,
  generic: GenericTooltips,
  role: RoleToolTips,
  bird: BirdTooltips,
};